import React, { useState } from "react";
import DraggablePanel from "./DraggablePanel";
import { usePhysicsStore } from "../../stores/physicsStore";
import { getPhysicsWorker } from "../../physics/workerHandle";
import { useIsMobile } from "../../hooks/useIsMobile";
import ActuatorControlsPanelBody, {
  CloseButton,
} from "./actuator-controls-panel/ActuatorControlsPanelBody";
import { useActuatorControlPanelData } from "./actuator-controls-panel/useActuatorControlPanelData";
import { PANEL_MODES } from "./actuator-controls-panel/constants";
import { useFocusTrap } from "../../hooks/useFocusTrap";

const ActuatorControlsPanel = ({
  open,
  onClose,
  motorGain,
  titleIcon: TitleIcon,
  mobilePinPanelId,
}) => {
  const isMobileLayout = useIsMobile();
  const isRunning = usePhysicsStore((s) => s.isRunning);
  const worker = getPhysicsWorker();
  const {
    actuatorList,
    groupedActuators,
    groupedJoints,
    joints,
    motorValues,
    posValues,
    sceneActions,
    selectedJoint,
    setActuatorMotorControl,
    setActuatorPositionTarget,
  } = useActuatorControlPanelData();

  const [collapsedGroups, setCollapsedGroups] = useState({});
  const mobileDialogRef = React.useRef(null);
  const lastIsRunningRef = React.useRef(isRunning);
  const autoPanelMode = isRunning ? PANEL_MODES.ACTUATORS : PANEL_MODES.JOINTS;
  const [panelMode, setPanelMode] = useState(() => autoPanelMode);
  const physicsStateChanged = lastIsRunningRef.current !== isRunning;
  const displayedPanelMode = physicsStateChanged ? autoPanelMode : panelMode;
  const effectivePanelMode = isMobileLayout ? autoPanelMode : displayedPanelMode;

  React.useLayoutEffect(() => {
    if (lastIsRunningRef.current === isRunning) return;
    lastIsRunningRef.current = isRunning;
    setPanelMode(autoPanelMode);
  }, [autoPanelMode, isRunning]);
  useFocusTrap(mobileDialogRef, isMobileLayout && open);

  const firstVisibleGroupKeyForMobile = React.useMemo(() => {
    if (!isMobileLayout) return null;
    if (effectivePanelMode === PANEL_MODES.JOINTS) {
      const found = groupedJoints.find(
        (group) => !(group.joints.length === 0 && group.key === "unassigned")
      );
      return found?.key ?? null;
    }
    const found = groupedActuators.find(
      (group) => !(group.motors.length + group.positions.length === 0 && group.key === "unassigned")
    );
    return found?.key ?? null;
  }, [isMobileLayout, effectivePanelMode, groupedJoints, groupedActuators]);

  const panelBody = (
    <ActuatorControlsPanelBody
      actuatorList={actuatorList}
      collapsedGroups={collapsedGroups}
      displayedPanelMode={displayedPanelMode}
      effectivePanelMode={effectivePanelMode}
      firstVisibleGroupKeyForMobile={firstVisibleGroupKeyForMobile}
      groupedActuators={groupedActuators}
      groupedJoints={groupedJoints}
      isMobileLayout={isMobileLayout}
      isRunning={isRunning}
      joints={joints}
      motorGain={motorGain}
      motorValues={motorValues}
      onClose={onClose}
      panelMode={panelMode}
      posValues={posValues}
      sceneActions={sceneActions}
      selectedJoint={selectedJoint}
      setActuatorMotorControl={setActuatorMotorControl}
      setActuatorPositionTarget={setActuatorPositionTarget}
      setCollapsedGroups={setCollapsedGroups}
      setPanelMode={setPanelMode}
      worker={worker}
    />
  );

  if (isMobileLayout) {
    if (!open) return null;
    const bottomInset = "max(12px, env(safe-area-inset-bottom, 0px))";
    const sheetMaxHeight = "min(85vh, calc(100dvh - 2rem))";
    return (
      <div
        ref={mobileDialogRef}
        className="fixed left-2 right-2 z-[28] rounded-t-xl pointer-events-auto overflow-x-hidden overflow-y-auto pt-1 pb-0.5"
        style={{
          bottom: bottomInset,
          maxHeight: sheetMaxHeight,
          backgroundColor: "var(--panel-surface)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={
          firstVisibleGroupKeyForMobile ? "actuator-controls-mobile-title" : undefined
        }
        aria-label={firstVisibleGroupKeyForMobile ? undefined : "Actuator controls"}
        tabIndex={-1}
      >
        {!firstVisibleGroupKeyForMobile ? (
          <div className="flex justify-end px-2 pb-1">
            <CloseButton
              onClose={onClose}
              className="flex h-7 w-7 items-center justify-center rounded text-gray-400 hover:text-white outline-none focus:outline-none focus-visible:outline-none"
            />
          </div>
        ) : null}
        <div className="space-y-0.5 px-0">{panelBody}</div>
      </div>
    );
  }

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Actuator Controls"
      titleIcon={TitleIcon}
      initialSize={{ width: 360, height: 620 }}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="p-3 space-y-3 overflow-y-auto flex-1 min-h-0">{panelBody}</div>
    </DraggablePanel>
  );
};

export default ActuatorControlsPanel;
